import { Box, Text, VStack, useColorMode } from '@chakra-ui/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import { Flag } from '../../types/chatTypes'

interface FlagBoxProps {
  flags?: Flag[]
}

type SeverityColors = { bg: string; border: string }

const getFlagColorsBySeverity = (
  severity: Flag['severity'],
  colorMode: 'light' | 'dark',
): SeverityColors => {
  const palette: Record<Flag['severity'], { light: SeverityColors; dark: SeverityColors }> = {
    info: {
      light: { bg: 'gray.100', border: 'gray.300' },
      dark: { bg: 'gray.700', border: 'gray.500' },
    },
    warning: {
      light: { bg: 'orange.100', border: 'orange.300' },
      dark: { bg: 'orange.700', border: 'orange.500' },
    },
    critical: {
      light: { bg: 'red.100', border: 'red.300' },
      dark: { bg: 'red.700', border: 'red.500' },
    },
  }
  return palette[severity]?.[colorMode] ?? palette.info[colorMode]
}

// One box per severity, most urgent first, so each box's colour reflects its own
// level instead of a blended one.
const SEVERITY_ORDER: Flag['severity'][] = ['critical', 'warning', 'info']

const FlagBox: React.FC<FlagBoxProps> = ({ flags }) => {
  const { colorMode } = useColorMode()
  const { t } = useTranslation()

  if (!flags || flags.length === 0) {
    return null
  }

  const groups = SEVERITY_ORDER.map(severity => ({
    severity,
    items: flags.filter(flag => flag.severity === severity),
  })).filter(group => group.items.length > 0)

  return (
    <VStack
      spacing={2}
      width='100%'
      maxWidth={['90%', '90%', '85%']}
      align='flex-start'
      mt={2}
      mb={2}
    >
      {groups.map(({ severity, items }) => {
        const colors = getFlagColorsBySeverity(severity, colorMode)
        return (
          <Box
            key={severity}
            px={4}
            py={3}
            borderRadius='6px'
            backgroundColor={colors.bg}
            border='1px solid'
            borderColor={colors.border}
            fontSize='sm'
            lineHeight='1.5'
            width='100%'
          >
            <Text fontSize='sm' fontWeight='semibold' mb={2}>
              {t(severity === 'info' ? 'common.flagInfo' : 'common.flagCaution')}
            </Text>
            <VStack spacing={2} align='flex-start'>
              {items.map(flag => (
                <Box key={flag.type}>
                  <Text
                    fontSize='xs'
                    fontWeight='bold'
                    textTransform='uppercase'
                    opacity={0.7}
                    mb={1}
                  >
                    {flag.display_name}
                  </Text>
                  <ReactMarkdown>{flag.message ?? ''}</ReactMarkdown>
                </Box>
              ))}
            </VStack>
          </Box>
        )
      })}
    </VStack>
  )
}

export default FlagBox
